import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bluetooth,
  Cpu,
  Database,
  HardDrive,
  HeartPulse,
  MemoryStick,
  Radio,
  Thermometer,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { useCaseQuery } from "hooks/useCaseDashboard";
import { formatDuration, issueList, parseUtc } from "utils/caseDashboard";
import { downsample } from "utils/caseLiveStats";
import { Card, EmptyState, QueryState, fmtDateTime } from "./parts";
import { issueLabel } from "./OverviewView";
import { Segmented } from "./live/shared";

// The case's own thresholds (app/case_health.py).
const TEMP_WARN_C = 70;
const TEMP_HOT_C = 80;
const RANGES = { "1h": 1, "6h": 6, "24h": 24, "48h": 48 };
const BUCKETS = 180;

const hhmm = (v) =>
  new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const dayTime = (v) =>
  new Date(v).toLocaleString([], {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

const STATUS_FILL = { ok: "#10B981", warn: "#F59E0B", critical: "#DC2626" };

const HealthView = ({ caseId, health }) => {
  const { t } = useTranslation();
  const [range, setRange] = useState("6h");
  const metrics = useCaseQuery(caseId, "metrics", { hours: 48 }, 30000);
  const hours = RANGES[range];

  const { points, sessions, from, to } = useMemo(() => {
    const rows = (metrics.data || [])
      .map((m) => ({ ...m, x: parseUtc(m.ts)?.getTime() }))
      .filter((m) => m.x);
    const end = rows.length ? rows[rows.length - 1].x : Date.now();
    const start = end - hours * 3600e3;
    const pts = downsample(rows, {
      from: start,
      to: end,
      buckets: BUCKETS,
      fields: [
        "cpu_temp_c",
        "mem_used_pct",
        "disk_used_pct",
        "load_1m",
        "adverts",
        "wearable_matches",
      ],
      maxFields: ["cpu_temp_c"],
    });
    // Contiguous runs of buckets that had a session, for shading.
    const runs = [];
    for (const p of pts) {
      const last = runs[runs.length - 1];
      if (p.session && last && p.x - last.to <= ((end - start) / BUCKETS) * 1.5)
        last.to = p.x;
      else if (p.session) runs.push({ from: p.x, to: p.x });
    }
    return { points: pts, sessions: runs, from: start, to: end };
  }, [metrics.data, hours]);

  if (!metrics.data?.length) {
    return (
      <div className="space-y-4">
        <QueryState query={metrics} />
        {!metrics.missing && (
          <Card>
            <EmptyState
              icon={HeartPulse}
              title={
                metrics.loading
                  ? t("caseDashboard.common.loading")
                  : t("caseDashboard.health.empty")
              }
              body={
                metrics.loading ? null : t("caseDashboard.health.emptyHint")
              }
            />
          </Card>
        )}
      </div>
    );
  }

  const tick = hours > 24 ? dayTime : hhmm;
  const shade = sessions.map((s) => (
    <ReferenceArea
      key={s.from}
      x1={s.from}
      x2={s.to}
      fill="#E07A3A"
      fillOpacity={0.06}
      ifOverflow="hidden"
    />
  ));
  const common = {
    data: points,
    margin: { top: 6, right: 12, bottom: 0, left: 0 },
  };
  const xAxis = (
    <XAxis
      dataKey="x"
      type="number"
      domain={[from, to]}
      tickFormatter={tick}
      tick={{ fontSize: 11, fill: "#9CA3AF" }}
      tickLine={false}
      axisLine={{ stroke: "#E5E7EB" }}
      minTickGap={48}
    />
  );
  const tooltip = (unit, digits = 1) => (
    <Tooltip
      isAnimationActive={false}
      labelFormatter={(v) => fmtDateTime(new Date(v))}
      formatter={(v, name) => [
        v == null ? "—" : `${Number(v).toFixed(digits)}${unit}`,
        name,
      ]}
      contentStyle={{
        borderRadius: 8,
        border: "1px solid #F3F4F6",
        fontSize: 12,
      }}
    />
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold text-[#111827]">
            {t("caseDashboard.tabs.health")}
          </h1>
          <p className="mt-0.5 text-[13px] text-[#6B7280]">
            {t("caseDashboard.health.subtitle")}
          </p>
        </div>
        <Segmented
          value={range}
          onChange={setRange}
          options={Object.keys(RANGES).map((r) => ({
            value: r,
            label: t(`caseDashboard.health.range_${r}`),
          }))}
        />
      </div>
      <QueryState query={metrics} />

      <NowGauges health={health?.data} />

      <StatusStrip points={points} from={from} to={to} tick={tick} />

      <div className="grid gap-4 xl:grid-cols-2">
        <ChartCard
          icon={Thermometer}
          title={t("caseDashboard.health.temperature")}
          hint={t("caseDashboard.health.temperatureHint")}
        >
          <AreaChart {...common}>
            <defs>
              <linearGradient id="hTemp" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#E07A3A" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#E07A3A" stopOpacity={0} />
              </linearGradient>
            </defs>
            {shade}
            <ReferenceArea
              y1={TEMP_WARN_C}
              y2={TEMP_HOT_C}
              fill="#FEF3C7"
              fillOpacity={0.6}
              ifOverflow="extendDomain"
            />
            <ReferenceArea
              y1={TEMP_HOT_C}
              y2={90}
              fill="#FEE2E2"
              fillOpacity={0.6}
              ifOverflow="extendDomain"
            />
            <CartesianGrid stroke="#F3F4F6" vertical={false} />
            {xAxis}
            <YAxis
              domain={[30, 90]}
              ticks={[30, 50, 70, 80, 90]}
              unit="°"
              tick={{ fontSize: 11, fill: "#9CA3AF" }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            {tooltip(" °C")}
            <Area
              type="monotone"
              dataKey="cpu_temp_c"
              name={t("caseDashboard.health.temperature")}
              stroke="#E07A3A"
              strokeWidth={2}
              fill="url(#hTemp)"
              isAnimationActive={false}
              connectNulls
            />
          </AreaChart>
        </ChartCard>

        <ChartCard
          icon={Radio}
          title={t("caseDashboard.health.wearablesHeard")}
          hint={t("caseDashboard.health.wearablesHeardHint")}
        >
          <BarChart {...common} barCategoryGap={1}>
            {shade}
            <CartesianGrid stroke="#F3F4F6" vertical={false} />
            {xAxis}
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "#9CA3AF" }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            {tooltip("", 0)}
            <Bar
              dataKey="wearable_matches"
              name={t("caseDashboard.health.matches")}
              fill="#10B981"
              isAnimationActive={false}
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ChartCard>

        <ChartCard
          icon={MemoryStick}
          title={t("caseDashboard.health.memoryAndStorage")}
          hint={t("caseDashboard.health.memoryHint")}
        >
          <AreaChart {...common}>
            {shade}
            <ReferenceLine y={85} stroke="#F59E0B" strokeDasharray="4 4" />
            <CartesianGrid stroke="#F3F4F6" vertical={false} />
            {xAxis}
            <YAxis
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              unit="%"
              tick={{ fontSize: 11, fill: "#9CA3AF" }}
              tickLine={false}
              axisLine={false}
              width={44}
            />
            {tooltip("%", 0)}
            <Area
              type="monotone"
              dataKey="mem_used_pct"
              name={t("caseDashboard.health.memory")}
              stroke="#3B82F6"
              strokeWidth={2}
              fill="#3B82F6"
              fillOpacity={0.08}
              isAnimationActive={false}
              connectNulls
            />
            <Area
              type="monotone"
              dataKey="disk_used_pct"
              name={t("caseDashboard.health.storage")}
              stroke="#8B5CF6"
              strokeWidth={2}
              fill="#8B5CF6"
              fillOpacity={0.05}
              isAnimationActive={false}
              connectNulls
            />
          </AreaChart>
        </ChartCard>

        <ChartCard
          icon={Cpu}
          title={t("caseDashboard.health.load")}
          hint={t("caseDashboard.health.loadHint")}
        >
          <AreaChart {...common}>
            {shade}
            <CartesianGrid stroke="#F3F4F6" vertical={false} />
            {xAxis}
            <YAxis
              tick={{ fontSize: 11, fill: "#9CA3AF" }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            {tooltip("", 2)}
            <Area
              type="monotone"
              dataKey="load_1m"
              name={t("caseDashboard.health.load")}
              stroke="#14B8A6"
              strokeWidth={2}
              fill="#14B8A6"
              fillOpacity={0.1}
              isAnimationActive={false}
              connectNulls
            />
          </AreaChart>
        </ChartCard>
      </div>
      {sessions.length > 0 && (
        <p className="flex items-center gap-2 text-[12px] text-[#6B7280]">
          <span className="inline-block h-3 w-5 rounded-sm bg-[#E07A3A]/10" />
          {t("caseDashboard.health.sessionShading")}
        </p>
      )}
    </div>
  );
};

const ChartCard = ({ icon: Icon, title, hint, children }) => (
  <div className="rounded-xl border border-[#F3F4F6] bg-white">
    <div className="border-b border-[#F3F4F6] px-4 py-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-[#111827]">
        <Icon size={15} className="text-[#9CA3AF]" />
        {title}
      </h2>
      {hint && <p className="mt-0.5 text-[12px] text-[#6B7280]">{hint}</p>}
    </div>
    <div className="h-56 px-2 pb-2 pt-3">
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  </div>
);

/** Healthy / attention / problem across the range, as one coloured strip. */
const StatusStrip = ({ points, from, to, tick }) => {
  const { t } = useTranslation();
  const span = Math.max(1, to - from);
  const issues = [...new Set(points.flatMap((p) => p.issues))];
  const bad = points.filter((p) => p.status && p.status !== "ok").length;
  return (
    <div className="rounded-xl border border-[#F3F4F6] bg-white px-4 py-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-[#111827]">
          {t("caseDashboard.health.history")}
        </h2>
        <span
          className={`text-[12px] font-medium ${
            bad ? "text-[#B4531A]" : "text-[#047857]"
          }`}
        >
          {bad
            ? t("caseDashboard.health.someIssues", {
                issues: issues.map((c) => issueLabel(t, c)).join(", "),
              })
            : t("caseDashboard.health.allGood")}
        </span>
      </div>
      <div className="relative h-4 overflow-hidden rounded-full bg-[#F3F4F6]">
        {points.map((p, i) => {
          const next = points[i + 1]?.x ?? to;
          return (
            <span
              key={p.x}
              title={`${fmtDateTime(new Date(p.x))} · ${t(
                `caseDashboard.health.status.${p.status || "ok"}`
              )}${
                p.issues.length
                  ? ` · ${p.issues.map((c) => issueLabel(t, c)).join(", ")}`
                  : ""
              }`}
              className="absolute inset-y-0"
              style={{
                left: `${((p.x - from) / span) * 100}%`,
                width: `${Math.max(0.3, ((next - p.x) / span) * 100)}%`,
                backgroundColor: STATUS_FILL[p.status] || "#E5E7EB",
              }}
            />
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-[#9CA3AF]">
        <span>{tick(from)}</span>
        <span>{tick(from + span / 2)}</span>
        <span>{t("caseDashboard.health.now")}</span>
      </div>
    </div>
  );
};

/** The case right now: one gauge per reading, with its warning level. */
const NowGauges = ({ health: h }) => {
  const { t } = useTranslation();
  if (!h || h.status === "unknown") return null;
  const issues = issueList(h.issues);
  const adapters = Object.values(h.adapters || {});
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
      <Gauge
        icon={Thermometer}
        label={t("caseDashboard.overview.temperature")}
        value={h.cpu_temp_c == null ? "—" : `${h.cpu_temp_c}°`}
        pct={h.cpu_temp_c == null ? null : (h.cpu_temp_c / 90) * 100}
        marks={[(TEMP_WARN_C / 90) * 100, (TEMP_HOT_C / 90) * 100]}
        tone={
          h.cpu_temp_c >= TEMP_HOT_C
            ? "bad"
            : h.cpu_temp_c >= TEMP_WARN_C
            ? "warn"
            : "ok"
        }
      />
      <Gauge
        icon={MemoryStick}
        label={t("caseDashboard.overview.memory")}
        value={pct(h.mem_used_pct)}
        pct={h.mem_used_pct}
        marks={[85]}
        tone={issues.includes("memory_low") ? "warn" : "ok"}
      />
      <Gauge
        icon={HardDrive}
        label={t("caseDashboard.overview.storage")}
        value={pct(h.disk_used_pct)}
        pct={h.disk_used_pct}
        marks={[85]}
        tone={issues.includes("disk_full") ? "warn" : "ok"}
      />
      <Gauge
        icon={Cpu}
        label={t("caseDashboard.health.load")}
        value={h.load_1m == null ? "—" : h.load_1m.toFixed(2)}
        pct={
          h.load_1m == null
            ? null
            : (h.load_1m / Math.max(1, h.cpu_count || 4)) * 100
        }
        tone={issues.includes("cpu_busy") ? "warn" : "ok"}
      />
      <Gauge
        icon={Bluetooth}
        label={t("caseDashboard.overview.radios")}
        value={
          adapters.length
            ? `${adapters.filter(Boolean).length}/${adapters.length}`
            : "—"
        }
        pct={
          adapters.length
            ? (adapters.filter(Boolean).length / adapters.length) * 100
            : null
        }
        tone={issues.some((c) => c.startsWith("adapter")) ? "bad" : "ok"}
      />
      <Gauge
        icon={Database}
        label={t("caseDashboard.overview.database")}
        value={
          h.db_ok
            ? t("caseDashboard.overview.dbOk")
            : t("caseDashboard.overview.dbDown")
        }
        pct={h.db_ok ? 100 : 0}
        tone={h.db_ok ? "ok" : "bad"}
        hint={
          h.app_uptime_s != null
            ? t("caseDashboard.health.appUp", {
                time: formatDuration(h.app_uptime_s),
              })
            : null
        }
      />
    </div>
  );
};

const pct = (v) => (v == null ? "—" : `${v}%`);
const TONE_COLOR = { ok: "#10B981", warn: "#F59E0B", bad: "#DC2626" };

const Gauge = ({
  icon: Icon,
  label,
  value,
  pct: p,
  marks = [],
  tone = "ok",
  hint,
}) => (
  <div className="rounded-xl border border-[#F3F4F6] bg-white p-3.5">
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1.5 text-[12px] text-[#6B7280]">
        <Icon size={13} />
        {label}
      </span>
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: TONE_COLOR[tone] }}
      />
    </div>
    <p className="mt-1.5 text-[22px] font-bold tabular-nums leading-none text-[#111827]">
      {value}
    </p>
    <div className="relative mt-3 h-1.5 rounded-full bg-[#F3F4F6]">
      {p != null && (
        <span
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: `${Math.min(100, Math.max(2, p))}%`,
            backgroundColor: TONE_COLOR[tone],
          }}
        />
      )}
      {marks.map((m) => (
        <span
          key={m}
          className="absolute -top-0.5 h-2.5 w-px bg-[#9CA3AF]"
          style={{ left: `${m}%` }}
        />
      ))}
    </div>
    {hint && <p className="mt-1.5 text-[11px] text-[#9CA3AF]">{hint}</p>}
  </div>
);

export default HealthView;
